'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import { filterFilesByAccept } from '@/shared/lib/files/filter-by-accept';
import { UploadIcon } from './UploadIcon';

interface UseFileDropTargetOptions {
  onFiles: (files: File[]) => void;
  /** `<input accept>`-style filter applied to the dropped files. */
  accept?: string;
  /** When false only the first matching file is passed on. */
  multiple?: boolean;
  disabled?: boolean;
}

export interface FileDropHandlers {
  onDragEnter: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
}

/** True when the drag carries OS files (not text, links or dnd-kit sortables). */
function isFileDrag(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

/**
 * Native file drag & drop for one element. Returns the drag-over flag and the
 * four handlers to spread on the element.
 *
 * Enter/leave are counted (dragenter fires again for every child the pointer
 * crosses) so the highlight does not flicker over nested children. Every
 * handled event stops propagation: when drop targets nest (a modal drop zone
 * rendered via a portal inside a page-wide target) the innermost one wins and
 * the files are never processed twice.
 */
export function useFileDropTarget({
  onFiles,
  accept,
  multiple = true,
  disabled = false,
}: UseFileDropTargetOptions): {
  isDragging: boolean;
  dropHandlers: FileDropHandlers;
} {
  const [isDragging, setIsDragging] = useState(false);
  const depth = useRef(0);

  const reset = useCallback(() => {
    depth.current = 0;
    setIsDragging(false);
  }, []);

  // A drag that ends outside the element (or is cancelled with Esc) never
  // delivers a matching dragleave, so the highlight would stick.
  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    return () => {
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', reset);
    };
  }, [isDragging, reset]);

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      depth.current += 1;
      if (!disabled) setIsDragging(true);
    },
    [disabled]
  );

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
    },
    [disabled]
  );

  const onDragLeave = useCallback((e: DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      reset();
      if (disabled) return;
      const files = filterFilesByAccept(e.dataTransfer.files, accept);
      const selected = multiple ? files : files.slice(0, 1);
      if (selected.length > 0) onFiles(selected);
    },
    [disabled, accept, multiple, onFiles, reset]
  );

  return {
    isDragging,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}

interface FileDropOverlayProps {
  multiple?: boolean;
  className?: string;
}

/** The teal "Drop files here" veil shown while files hover over a target. */
export function FileDropOverlay({
  multiple = true,
  className,
}: FileDropOverlayProps) {
  const t = useTranslations('fileUpload');
  return (
    <div
      className={cn(
        // Frosted veil so the "Drop files here" label never fights the content
        // underneath (empty-state captions, tile labels); teal tint on top.
        'pointer-events-none absolute inset-0 z-40 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-background/70 backdrop-blur-sm',
        className
      )}
    >
      <div className="absolute inset-0 rounded-[inherit] bg-primary/10" />
      <div className="relative text-center">
        <UploadIcon className="mx-auto mb-2 h-16 w-16 animate-pulse text-primary" />
        <p className="font-semibold text-primary">
          {multiple ? t('dropFilesHere') : t('dropFileHere')}
        </p>
      </div>
    </div>
  );
}

interface FileDropTargetProps extends UseFileDropTargetOptions {
  children: ReactNode;
  className?: string;
  /** Extra classes for the overlay (e.g. a rounder corner radius). */
  overlayClassName?: string;
}

/**
 * Wraps a region so OS files can be dropped anywhere on it. While the target is
 * mounted and enabled, a drop that lands elsewhere on the page is swallowed
 * instead of letting the browser navigate to the dropped file.
 */
export function FileDropTarget({
  children,
  className,
  overlayClassName,
  disabled = false,
  multiple = true,
  ...options
}: FileDropTargetProps) {
  const { isDragging, dropHandlers } = useFileDropTarget({
    ...options,
    multiple,
    disabled,
  });

  useEffect(() => {
    if (disabled) return;
    const prevent = (e: globalThis.DragEvent) => {
      if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) {
        e.preventDefault();
      }
    };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, [disabled]);

  return (
    <div className={cn('relative', className)} {...dropHandlers}>
      {children}
      {isDragging && (
        <FileDropOverlay multiple={multiple} className={overlayClassName} />
      )}
    </div>
  );
}
